import { useQuery } from "@tanstack/react-query";
import { useOrganization } from "@clerk/clerk-react";
import { apiRequest } from "@/lib/queryClient";
import type { Product } from "@/types/product";
import type { HajocaInventoryResponse } from "@shared/types/hajoca";
import {
  parseHajocaPriceToCents,
  parseHajocaQuantity,
} from "@shared/hajoca-utils";

export interface BranchInventory {
  branchId: string;
  branchName?: string;
  qty: number | null;
  /** Live price in integer cents, same unit as product.currentPrice */
  priceCents: number | null;
  uom: string;
}

/** hajocaProductId -> inventory at the org's branch, for O(1) render lookup */
export type InventoryByProduct = Map<string, BranchInventory>;

function toLookup(res: HajocaInventoryResponse): InventoryByProduct {
  const branchNames = new Map<string, string>();
  for (const entry of res.included ?? []) {
    const branch = entry?.data;
    if (branch?.id) {
      branchNames.set(branch.id, branch.attributes?.name);
    }
  }

  const byProduct: InventoryByProduct = new Map();
  for (const item of res.data ?? []) {
    const productId = item?.relationships?.product?.data?.id;
    const branchId = item?.relationships?.branch?.data?.id;
    if (!productId || !branchId) continue;

    byProduct.set(productId, {
      branchId,
      branchName: branchNames.get(branchId),
      qty: parseHajocaQuantity(item.attributes.qty),
      priceCents: parseHajocaPriceToCents(item.attributes.price),
      uom: item.attributes.uom,
    });
  }
  return byProduct;
}

/**
 * Merge live inventory over cached product pricing for rendering.
 *
 * Live values win where present; products without a live row pass through
 * unchanged. The availability status is derived from live qty — the branch
 * stock evidence — because the cached enum comes from /v1/products, whose
 * branch resolution has proven unreliable. Exceptions:
 * - discontinued is preserved: inventory data cannot know a product's
 *   lifecycle status, and resurrecting a discontinued product would be worse
 *   than a stale badge
 * - an unparseable live qty keeps the cached status
 */
export function applyLiveInventory(
  products: Product[],
  byProduct: InventoryByProduct,
): Product[] {
  return products.map((product) => {
    const live = product.hajocaProductId
      ? byProduct.get(product.hajocaProductId)
      : undefined;
    if (!live) return product;

    const currentAvailability =
      product.currentAvailability === "discontinued" || live.qty == null
        ? product.currentAvailability
        : live.qty > 0
          ? ("in_stock" as const)
          : ("out_of_stock" as const);

    return {
      ...product,
      currentPrice: live.priceCents ?? product.currentPrice,
      currentQuantityAvailable: live.qty ?? product.currentQuantityAvailable,
      currentAvailability,
    };
  });
}

/**
 * Fetch live inventory (qty / price / uom) for a set of products at the org's
 * Hajoca branch.
 *
 * Backed by POST /api/hajoca/inventory — the server resolves the org's
 * account and branch, so the client only sends product ids. Products are
 * mapped to hajocaProductId internally and prices are converted to integer
 * cents, so results drop into the same render path as cached
 * product.currentPrice.
 *
 * The query is keyed on (org, sorted hajoca product ids), so repeated lookups
 * for the same screen are cached and de-duplicated, and the cache clears on
 * org switch. Keep `products` to what's actually on screen. Pass an empty
 * list (or enabled: false) to disable.
 */
export const useHajocaInventory = (
  products: Product[],
  options?: { enabled?: boolean },
) => {
  const { organization } = useOrganization();

  const hajocaIds = products
    .map((p) => p.hajocaProductId)
    .filter((id): id is string => !!id);
  const sortedIds = [...new Set(hajocaIds)].sort();

  return useQuery({
    queryKey: ["hajoca-inventory", organization?.id, sortedIds],
    enabled:
      (options?.enabled ?? true) && !!organization?.id && sortedIds.length > 0,
    queryFn: async () => {
      const response = await apiRequest("POST", "/api/hajoca/inventory", {
        productIds: sortedIds,
      });
      const raw = (await response.json()) as HajocaInventoryResponse;
      return { raw, byProduct: toLookup(raw) };
    },
    staleTime: 60 * 1000, // prices move, but not within a browsing session
    gcTime: 5 * 60 * 1000,
    // External vendor call at 1-2s — don't refire on every tab focus
    refetchOnWindowFocus: false,
  });
};
